
import { v4 as uuidv4 } from "uuid";
import { GAOptimizationRequest, RoomWithWalls, Wall } from "../services/api";

// Helper to generate a consistent key for a wall segment (independent of direction)
const getWallKey = (x1: number, y1: number, x2: number, y2: number) => {
    const p1 = `${x1},${y1}`;
    const p2 = `${x2},${y2}`;
    return p1 < p2 ? `${p1}-${p2}` : `${p2}-${p1}`;
};

export const convertSpaceResultToGARequest = (
    spaceResult: any,
    corridorWidth: number = 3
): GAOptimizationRequest => {
    const floor = spaceResult.data.floor;
    const placements = spaceResult.data.placements;

    const regionDims = (() => {
        const regions = floor?.regions;
        if (!Array.isArray(regions) || regions.length === 0) return null;

        const parsed = regions
            .map((region: any) => ({
                x: Number(region?.x),
                y: Number(region?.y),
                width: Number(region?.width),
                height: Number(region?.height),
            }))
            .filter((region) => Number.isFinite(region.x) && Number.isFinite(region.y) && Number.isFinite(region.width) && Number.isFinite(region.height));

        if (parsed.length === 0) return null;

        const minX = Math.min(...parsed.map((region) => region.x));
        const minY = Math.min(...parsed.map((region) => region.y));
        const maxX = Math.max(...parsed.map((region) => region.x + region.width));
        const maxY = Math.max(...parsed.map((region) => region.y + region.height));

        return {
            plotWidth: Math.max(0, maxX - minX),
            plotHeight: Math.max(0, maxY - minY),
        };
    })();

    const plotWidth = regionDims?.plotWidth || Number(floor?.width) || 0;
    const plotHeight = regionDims?.plotHeight || Number(floor?.height) || 0;

    const gaRooms: RoomWithWalls[] = [];
    const allWalls: Wall[] = [];
    const labels: { x: number, y: number, text: string, roomId: string }[] = [];

    // Map to track shared walls: keys -> Wall object
    const wallMap = new Map<string, Wall>();

    placements.forEach((placement: any) => {
        const roomName = placement.name;
        // Generate a stable ID based on name or random if needed
        // Using name-hash style or just random for now since Space doesn't return IDs for placements usually (just names)
        const roomId = `room-${roomName.toLowerCase().replace(/\s+/g, '-')}-${uuidv4().slice(0, 4)}`;

        const x = placement.x;
        const y = placement.y;
        const w = placement.width;
        const h = placement.height;

        // Define 4 walls for the rectangular room
        const rawWalls = [
            { id: `wall-${roomId}-left`, x1: x, y1: y, x2: x, y2: y + h },
            { id: `wall-${roomId}-top`, x1: x, y1: y, x2: x + w, y2: y },
            { id: `wall-${roomId}-right`, x1: x + w, y1: y, x2: x + w, y2: y + h },
            { id: `wall-${roomId}-bottom`, x1: x, y1: y + h, x2: x + w, y2: y + h }
        ];

        const roomWalls: Wall[] = [];

        rawWalls.forEach(rw => {
            const key = getWallKey(rw.x1, rw.y1, rw.x2, rw.y2);
            let wall = wallMap.get(key);

            if (wall) {
                // Shared wall found! Add this room to it.
                if (!wall.roomIds.includes(roomId)) {
                    wall.roomIds.push(roomId);
                }
            } else {
                // New wall
                wall = {
                    id: rw.id,
                    roomIds: [roomId],
                    x1: rw.x1,
                    y1: rw.y1,
                    x2: rw.x2,
                    y2: rw.y2
                };
                wallMap.set(key, wall);
                allWalls.push(wall);
            }
            roomWalls.push(wall!);
        });

        gaRooms.push({
            id: roomId,
            name: roomName,
            color: '#FF9A56', // Default color
            walls: roomWalls
        });

        labels.push({
            x: x + w / 2,
            y: y + h / 2,
            text: roomName,
            roomId: roomId
        });
    });

    return {
        request_id: `req-ga-${uuidv4()}`,
        engine: "GA_FloorPlan",
        params: {
            plot_width: plotWidth,
            plot_height: plotHeight,
            corridor_width: corridorWidth,
            ga_config: {
                population_size: 50,
                num_generations: 100,
                mutation_rate: 0.1
            },
            rooms: gaRooms,
            walls: allWalls,
            labels: labels,
            windows: [],
            doors: []
        }
    };
};
